import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEventHandler,
} from "react"
import Button from "../../button/button"
import EditorOverlay from "./EditorOverlay"
import type { PendingEditorImage } from "../types"

type ImageInsertModalProps = {
  image: PendingEditorImage
  onClose: () => void
  onInsert: (size: { width: number; height: number }) => void
}

const PREVIEW_MAX_WIDTH = 480
const PREVIEW_MAX_HEIGHT = 360
const MIN_SIZE = 40

const fitPreviewDisplaySize = (
  width: number,
  height: number,
  maxWidth = PREVIEW_MAX_WIDTH,
  maxHeight = PREVIEW_MAX_HEIGHT
) => {
  const scale = Math.min(1, maxWidth / width, maxHeight / height)
  return {
    width: Math.max(MIN_SIZE, Math.round(width * scale)),
    height: Math.max(MIN_SIZE, Math.round(height * scale)),
  }
}

const SIZE_PRESETS = [
  { label: "Small", percent: 25 },
  { label: "Medium", percent: 50 },
  { label: "Large", percent: 75 },
  { label: "Original", percent: 100 },
] as const

const getInitialSize = (naturalWidth: number, naturalHeight: number) => {
  if (naturalWidth <= PREVIEW_MAX_WIDTH) {
    return { width: naturalWidth, height: naturalHeight }
  }

  const width = PREVIEW_MAX_WIDTH
  const height = Math.round(PREVIEW_MAX_WIDTH * (naturalHeight / naturalWidth))
  return { width, height }
}

const clampSize = (
  width: number,
  height: number,
  naturalWidth: number,
  naturalHeight: number
) => {
  const ratio = naturalWidth / naturalHeight
  let nextWidth = Math.max(MIN_SIZE, Math.min(naturalWidth, Math.round(width)))
  let nextHeight = Math.max(
    MIN_SIZE,
    Math.min(naturalHeight, Math.round(nextWidth / ratio))
  )

  if (nextHeight > naturalHeight) {
    nextHeight = naturalHeight
    nextWidth = Math.round(nextHeight * ratio)
  }

  return { width: nextWidth, height: nextHeight }
}

const ImageInsertModal = ({
  image,
  onClose,
  onInsert,
}: ImageInsertModalProps) => {
  const { src, alt, naturalWidth, naturalHeight } = image
  const aspectRatio = naturalWidth / naturalHeight
  const initialSize = getInitialSize(naturalWidth, naturalHeight)

  const [width, setWidth] = useState(initialSize.width)
  const [height, setHeight] = useState(initialSize.height)
  const [activePreset, setActivePreset] = useState<number | null>(null)
  const [stageBounds, setStageBounds] = useState({
    width: PREVIEW_MAX_WIDTH,
    height: PREVIEW_MAX_HEIGHT,
  })
  const previewRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return

    const updateBounds = () => {
      const nextWidth = Math.max(
        MIN_SIZE,
        Math.floor(stage.clientWidth - 48)
      )
      const nextHeight = Math.max(
        MIN_SIZE,
        Math.floor(stage.clientHeight - 48)
      )
      setStageBounds({ width: nextWidth, height: nextHeight })
    }

    updateBounds()

    const observer = new ResizeObserver(updateBounds)
    observer.observe(stage)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const next = getInitialSize(naturalWidth, naturalHeight)
    setWidth(next.width)
    setHeight(next.height)
    setActivePreset(null)
  }, [src, naturalWidth, naturalHeight])

  const applySize = useCallback(
    (nextWidth: number, nextHeight?: number) => {
      const resolvedHeight =
        nextHeight ?? Math.round(nextWidth / aspectRatio)
      const clamped = clampSize(
        nextWidth,
        resolvedHeight,
        naturalWidth,
        naturalHeight
      )
      setWidth(clamped.width)
      setHeight(clamped.height)
      setActivePreset(null)
    },
    [aspectRatio, naturalWidth, naturalHeight]
  )

  const applyPreset = (percent: number) => {
    const nextWidth = Math.round((naturalWidth * percent) / 100)
    const nextHeight = Math.round((naturalHeight * percent) / 100)
    setWidth(nextWidth)
    setHeight(nextHeight)
    setActivePreset(percent)
  }

  const handleWidthChange = (value: string) => {
    const parsed = Number.parseInt(value, 10)
    if (Number.isNaN(parsed)) return
    applySize(parsed)
  }

  const handleHeightChange = (value: string) => {
    const parsed = Number.parseInt(value, 10)
    if (Number.isNaN(parsed)) return
    applySize(Math.round(parsed * aspectRatio), parsed)
  }

  const handleResizePointerDown: PointerEventHandler<HTMLButtonElement> = (
    event
  ) => {
    event.preventDefault()
    event.stopPropagation()

    const startX = event.clientX
    const startY = event.clientY
    const startWidth = width
    const startHeight = height

    const handlePointerMove = (moveEvent: globalThis.PointerEvent) => {
      const deltaX = moveEvent.clientX - startX
      const deltaY = moveEvent.clientY - startY
      const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY
      applySize(startWidth + delta, startHeight + delta)
    }

    const handlePointerUp = () => {
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerup", handlePointerUp)
    }

    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerup", handlePointerUp)
  }

  const handleInsert = () => {
    onInsert({ width, height })
    onClose()
  }

  const previewDisplaySize = fitPreviewDisplaySize(
    width,
    height,
    stageBounds.width,
    stageBounds.height
  )

  return (
    <EditorOverlay
      isOpen
      onClose={onClose}
      className='tiptap-editor__image-modal'
      panelRef={previewRef}
    >
      <div className='tiptap-editor__image-modal-header'>
        <div>
          <h3>Insert Image</h3>
          <p>Resize the preview, pick a preset, or enter exact dimensions.</p>
        </div>
        <div className='tiptap-editor__image-modal-actions'>
          <Button type='secondary' text='Cancel' onClick={onClose} />
          <Button text='Insert' onClick={handleInsert} />
        </div>
      </div>

      <div className='tiptap-editor__image-preview-stage' ref={stageRef}>
        <div
          className='tiptap-editor__image-preview-box'
          style={{
            width: previewDisplaySize.width,
            height: previewDisplaySize.height,
          }}
        >
          <img
            src={src}
            alt={alt}
            className='tiptap-editor__image-preview-img'
            draggable={false}
          />
          <button
            type='button'
            className='tiptap-editor__image-preview-handle'
            aria-label='Resize image'
            onPointerDown={handleResizePointerDown}
          />
        </div>
      </div>

      <div className='tiptap-editor__image-size-controls'>
        <div className='tiptap-editor__image-size-presets'>
          {SIZE_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type='button'
              className={`tiptap-editor__image-size-preset${
                activePreset === preset.percent ? " is-active" : ""
              }`}
              onClick={() => applyPreset(preset.percent)}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className='tiptap-editor__image-size-inputs'>
          <label>
            <span>Width</span>
            <div className='tiptap-editor__image-size-field'>
              <input
                type='number'
                min={MIN_SIZE}
                max={naturalWidth}
                value={width}
                onChange={(event) => handleWidthChange(event.target.value)}
              />
              <span>px</span>
            </div>
          </label>
          <span className='tiptap-editor__image-size-separator'>×</span>
          <label>
            <span>Height</span>
            <div className='tiptap-editor__image-size-field'>
              <input
                type='number'
                min={MIN_SIZE}
                max={naturalHeight}
                value={height}
                onChange={(event) => handleHeightChange(event.target.value)}
              />
              <span>px</span>
            </div>
          </label>
          <span className='tiptap-editor__image-size-original'>
            Original: {naturalWidth} × {naturalHeight}
          </span>
        </div>
      </div>
    </EditorOverlay>
  )
}

export default ImageInsertModal
